import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Box,
  Tooltip,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Avatar,
  AvatarGroup,
  Stack,
  LinearProgress,
  Badge,
} from '@mui/material';
import {
  MoreVert,
  PlaylistPlay,
  PlayArrow,
  Pause,
  Edit,
  ContentCopy,
  Delete,
  Schedule,
  Devices,
  Visibility,
  Archive,
  Unarchive,
  PlaylistAdd,
  Shuffle,
  Repeat,
  Emergency,
  Assignment,
  AccessTime,
  Person,
  CalendarToday,
  PlayCircle,
  StopCircle,
} from '@mui/icons-material';

import { PlaylistStatus, PlaylistType, UserRole } from "../../types";
import type { Playlist } from "../../types";
import { convertMediaUrl } from "../../config/environment";

interface PlaylistCardProps {
  playlist: Playlist;
  onPlay?: (playlist: Playlist) => void;
  onStop?: (playlist: Playlist) => void;
  onEdit?: (playlist: Playlist) => void;
  onDuplicate?: (playlist: Playlist) => void;
  onDelete?: (playlist: Playlist) => void;
  onArchive?: (playlist: Playlist) => void;
  onUnarchive?: (playlist: Playlist) => void;
  onSchedule?: (playlist: Playlist) => void;
  onAssignDevices?: (playlist: Playlist) => void;
  onViewDetails?: (playlist: Playlist) => void;
  onPreview?: (playlist: Playlist) => void;
  currentUserRole?: UserRole;
}

const PlaylistCard: React.FC<PlaylistCardProps> = ({
  playlist,
  onPlay,
  onStop,
  onEdit,
  onDuplicate,
  onDelete,
  onArchive,
  onUnarchive,
  onSchedule,
  onAssignDevices,
  onViewDetails,
  onPreview,
  currentUserRole = UserRole.VIEWER,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  const getStatusLabel = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'draft': return 'Черновик';
      case 'active': return 'Активный';
      case 'archived': return 'Архивированный';
      default: return 'Активный';
    }
  };

  const getTypeLabel = (type: string): string => {
    switch (type.toLowerCase()) {
      case 'standard': return 'Стандартный';
      case 'emergency': return 'Экстренный';
      case 'template': return 'Шаблон';
      default: return 'Стандартный';
    }
  };

  const getStatusColor = (status: PlaylistStatus) => {
    switch (status) {
      case PlaylistStatus.ACTIVE:
        return 'success';
      case PlaylistStatus.DRAFT:
        return 'warning';
      case PlaylistStatus.ARCHIVED:
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: PlaylistStatus) => {
    switch (status) {
      case PlaylistStatus.ACTIVE:
        return <PlayCircle fontSize="small" />;
      case PlaylistStatus.DRAFT:
        return <Edit fontSize="small" />;
      case PlaylistStatus.ARCHIVED:
        return <Archive fontSize="small" />;
      default:
        return <PlaylistPlay fontSize="small" />;
    }
  };

  const getTypeIcon = (type: PlaylistType) => {
    switch (type) {
      case PlaylistType.EMERGENCY:
        return <Emergency fontSize="small" />;
      case PlaylistType.TEMPLATE:
        return <Assignment fontSize="small" />;
      case PlaylistType.STANDARD:
      default:
        return <PlaylistPlay fontSize="small" />;
    }
  };

  const getTypeColor = (type: PlaylistType) => {
    switch (type) {
      case PlaylistType.EMERGENCY:
        return 'error';
      case PlaylistType.TEMPLATE:
        return 'info';
      case PlaylistType.STANDARD:
      default:
        return 'primary';
    }
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return t('common.today');
    if (diffDays === 1) return t('common.yesterday');
    if (diffDays < 7) return t('common.daysAgo', { count: diffDays });
    return date.toLocaleDateString();
  };

  const canEdit = () => {
    return [UserRole.ADMIN, UserRole.MANAGER, UserRole.SCHEDULER].includes(currentUserRole);
  };

  const canDelete = () => {
    return [UserRole.ADMIN, UserRole.MANAGER].includes(currentUserRole);
  };

  const canPlay = () => {
    return [UserRole.ADMIN, UserRole.MANAGER, UserRole.SCHEDULER, UserRole.ENGINEER].includes(currentUserRole);
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
      }}
    >
      {/* Заголовок карточки */}
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          {/* Аватар с иконкой типа */}
          <Avatar
            sx={{
              bgcolor: getTypeColor(playlist.type),
              width: 48,
              height: 48,
            }}
          >
            {getTypeIcon(playlist.type)}
          </Avatar>

          {/* Основная информация */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexGrow: 1,
                }}
              >
                {playlist.name}
              </Typography>

              {/* Бейджи для особых свойств */}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {playlist.is_default && (
                  <Tooltip title={t('playlists.defaultPlaylist')}>
                    <Chip
                      label="Default"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Tooltip>
                )}
                {playlist.loop_enabled && (
                  <Tooltip title={t('playlists.loopEnabled')}>
                    <Repeat fontSize="small" color="action" />
                  </Tooltip>
                )}
                {playlist.shuffle_enabled && (
                  <Tooltip title={t('playlists.shuffleEnabled')}>
                    <Shuffle fontSize="small" color="action" />
                  </Tooltip>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Chip
                  icon={getStatusIcon(playlist.status || 'active')}
                  label={getStatusLabel(playlist.status || 'active')}
                  color={getStatusColor(playlist.status || 'active')}
                  size="small"
                  variant="outlined"
                />
                <Typography variant="caption" color="text.secondary">
                  {getTypeLabel(playlist.type || 'standard')}
                </Typography>
            </Box>

            {playlist.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 1,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {playlist.description}
              </Typography>
            )}
          </Box>

          {/* Меню действий */}
          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreVert />
          </IconButton>
        </Box>

        {/* Статистика плейлиста */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PlaylistAdd fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {t('playlists.itemsCount', { count: playlist.items_count })}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {formatDuration(playlist.total_duration)}
              </Typography>
            </Box>

          </Stack>
        </Box>

        {/* Превью медиафайлов */}
        {playlist.preview_media && playlist.preview_media.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              {t('playlists.preview')}
            </Typography>
            <AvatarGroup
              max={4}
              sx={{
                '& .MuiAvatar-root': { width: 32, height: 32, fontSize: '0.875rem' },
              }}
            >
              {playlist.preview_media.map((media, index) => (
                <Avatar
                  key={media.id}
                  src={media.thumbnail_url ? convertMediaUrl(media.thumbnail_url) : undefined}
                  alt={media.title}
                  sx={{
                    bgcolor: media.thumbnail_url ? 'transparent' : 'primary.main',
                  }}
                >
                  {!media.thumbnail_url && (media.type === 'video' ? '▶️' : '🖼️')}
                </Avatar>
              ))}
            </AvatarGroup>
          </Box>
        )}

        {/* Информация об авторе и дате */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Person fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {playlist.creator?.full_name || t('common.unknown')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarToday fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {formatRelativeTime(playlist.created_at)}
            </Typography>
          </Box>
        </Box>

        {/* Последнее воспроизведение */}
        {playlist.last_played && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('playlists.lastPlayed')}: {formatRelativeTime(playlist.last_played)}
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* Действия */}
      <CardActions sx={{ mt: 'auto', px: 2, pb: 2 }}>
        <Button
          size="small"
          onClick={() => onViewDetails?.(playlist)}
          startIcon={<Visibility />}
        >
          {t('common.details')}
        </Button>

        {canPlay() && playlist.status === PlaylistStatus.ACTIVE && (
          <Button
            size="small"
            onClick={() => onPlay?.(playlist)}
            startIcon={<PlayArrow />}
            color="primary"
          >
            {t('playlists.play')}
          </Button>
        )}

        {canEdit() && (
          <Button
            size="small"
            onClick={() => onEdit?.(playlist)}
            startIcon={<Edit />}
          >
            {t('common.edit')}
          </Button>
        )}
      </CardActions>

      {/* Контекстное меню */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {onViewDetails && (
          <MenuItem onClick={() => handleAction(() => onViewDetails(playlist))}>
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('playlists.viewDetails')}</ListItemText>
          </MenuItem>
        )}

        {onPreview && playlist.items_count > 0 && (
          <MenuItem onClick={() => handleAction(() => onPreview(playlist))}>
            <ListItemIcon>
              <PlayCircle fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('playlists.preview')}</ListItemText>
          </MenuItem>
        )}

        {canEdit() && onEdit && (
          <MenuItem onClick={() => handleAction(() => onEdit(playlist))}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.edit')}</ListItemText>
          </MenuItem>
        )}

        {canEdit() && onDuplicate && (
          <MenuItem onClick={() => handleAction(() => onDuplicate(playlist))}>
            <ListItemIcon>
              <ContentCopy fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('playlists.duplicate')}</ListItemText>
          </MenuItem>
        )}

        <Divider />

        {canPlay() && onPlay && playlist.status === PlaylistStatus.ACTIVE && (
          <MenuItem onClick={() => handleAction(() => onPlay(playlist))}>
            <ListItemIcon>
              <PlayArrow fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('playlists.play')}</ListItemText>
          </MenuItem>
        )}

        {canPlay() && onStop && (
          <MenuItem onClick={() => handleAction(() => onStop(playlist))}>
            <ListItemIcon>
              <StopCircle fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('playlists.stop')}</ListItemText>
          </MenuItem>
        )}

        {canEdit() && onSchedule && (
          <MenuItem onClick={() => handleAction(() => onSchedule(playlist))}>
            <ListItemIcon>
              <Schedule fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('playlists.schedule')}</ListItemText>
          </MenuItem>
        )}

        {canEdit() && onAssignDevices && (
          <MenuItem onClick={() => handleAction(() => onAssignDevices(playlist))}>
            <ListItemIcon>
              <Devices fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('playlists.assignDevices')}</ListItemText>
          </MenuItem>
        )}

        <Divider />

        {canEdit() && playlist.status !== PlaylistStatus.ARCHIVED && onArchive && (
          <MenuItem onClick={() => handleAction(() => onArchive(playlist))}>
            <ListItemIcon>
              <Archive fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('playlists.archive')}</ListItemText>
          </MenuItem>
        )}

        {canEdit() && playlist.status === PlaylistStatus.ARCHIVED && onUnarchive && (
          <MenuItem onClick={() => handleAction(() => onUnarchive(playlist))}>
            <ListItemIcon>
              <Unarchive fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('playlists.unarchive')}</ListItemText>
          </MenuItem>
        )}

        {canDelete() && onDelete && [
          <Divider key="delete-divider" />,
          <MenuItem key="delete" onClick={() => handleAction(() => onDelete(playlist))}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.delete')}</ListItemText>
          </MenuItem>
        ]}
      </Menu>
    </Card>
  );
};

export default PlaylistCard;



